import { Component, type ReactNode } from 'react';
import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import { Bug, House, RefreshCw } from 'lucide-react';
import { StateCard } from '@/components/common/StateCard';
import { Button } from '@/components/ui/button';

interface ErrorBoundaryProps {
  moduleName?: string | undefined;
  children: ReactNode;
}

interface InnerErrorBoundaryProps extends ErrorBoundaryProps {
  onReset?: (() => void) | undefined;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class InnerErrorBoundary extends Component<InnerErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: InnerErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  handleReset = () => {
    this.props.onReset?.();
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <StateCard
          icon={Bug}
          tone="danger"
          title={
            this.props.moduleName
              ? `${this.props.moduleName} could not be shown`
              : 'Something went wrong on this page'
          }
          description="Try again, and tell the team if it keeps happening."
          fill
          actions={
            <>
              <Button size="sm" onClick={this.handleReset} className="gap-1.5">
                <RefreshCw className="size-3.5" />
                Try again
              </Button>
              <Button variant="outline" size="sm" onClick={this.handleGoHome} className="gap-1.5">
                <House className="size-3.5" />
                Go to dashboard
              </Button>
            </>
          }
        />
      );
    }

    return this.props.children;
  }
}

export function ErrorBoundary({ moduleName, children }: ErrorBoundaryProps) {
  const { reset } = useQueryErrorResetBoundary();
  return (
    <InnerErrorBoundary moduleName={moduleName} onReset={reset}>
      {children}
    </InnerErrorBoundary>
  );
}
